/* Warehouse Gear Hub — cart drawer behaviours. Loaded (defer) by
   snippets/modal-popup.liquid on every page.

   <wgh-cart-recs> (snippets/cart-items.liquid) fetches sections/wgh-cart-recs
   through the Section Rendering API for the most recently added line item:
   "complementary" first (curated in Search & Discovery), then Shopify's
   automatic "related" list. Rows already in the cart are dropped; for the
   related fallback, products of a different type than the added item come
   first (accessories before alternatives), cheapest first.

   The row's Add button posts to /cart/add.js and then raises the theme's
   own `updateCart` event, which modal.js turns into main-cart.update()
   (line items + subtotal) and cart-counter.js into a header count refresh. */
(() => {
  'use strict';

  const memo = {
    get(key) { try { return sessionStorage.getItem(key); } catch (e) { return null; } },
    set(key, value) { try { sessionStorage.setItem(key, value); } catch (e) { /* private mode */ } }
  };

  class WghCartRecs extends HTMLElement {
    connectedCallback() {
      const pid = this.dataset.productId;
      if (!pid || this.dataset.loadedFor === pid) return;
      this.dataset.loadedFor = pid;
      this.load(pid).catch((err) => { console.error(err); this.hidden = true; });
    }

    async load(pid) {
      const url = this.dataset.url || '/recommendations/products';
      const section = this.dataset.section || 'wgh-cart-recs';
      const count = parseInt(this.dataset.count, 10) || 3;
      const inCart = new Set((this.dataset.inCart || '').split(',').map((s) => s.trim()).filter(Boolean));
      const addedType = (this.dataset.productType || '').trim().toLowerCase();
      const intents = memo.get(`wgh-recs-nocomp:${pid}`) ? ['related'] : ['complementary', 'related'];

      for (const intent of intents) {
        const res = await fetch(`${url}?section_id=${encodeURIComponent(section)}&product_id=${encodeURIComponent(pid)}&limit=10&intent=${intent}`, {
          headers: { Accept: 'text/html' }
        });
        if (!res.ok) continue;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const list = doc.querySelector('[data-wgh-recs-list]');
        let items = list ? Array.from(list.children).filter((li) => !inCart.has(li.dataset.productId)) : [];
        if (!items.length) {
          if (intent === 'complementary') memo.set(`wgh-recs-nocomp:${pid}`, '1');
          continue;
        }
        if (intent === 'related') {
          const price = (li) => Number(li.dataset.price) || 0;
          const other = items.filter((li) => addedType && (li.dataset.type || '').trim().toLowerCase() !== addedType);
          const same = items.filter((li) => !other.includes(li));
          items = other.sort((a, b) => price(a) - price(b)).concat(same.sort((a, b) => price(a) - price(b)));
        }
        items = items.slice(0, count);

        const heading = document.createElement('h3');
        heading.className = 'wgh-cart-recs__heading';
        heading.textContent = (intent === 'complementary' ? list.dataset.headingComplementary : list.dataset.headingRelated) || '';
        const ul = document.createElement('ul');
        ul.className = 'wgh-cart-recs__list';
        items.forEach((li) => ul.appendChild(document.importNode(li, true)));
        this.replaceChildren(heading, ul);
        this.hidden = false;
        return;
      }
      this.hidden = true;
    }
  }
  if (!customElements.get('wgh-cart-recs')) customElements.define('wgh-cart-recs', WghCartRecs);

  /* Order-note toggle in the drawer footer (sections/main-cart.liquid). */
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-wgh-notes-toggle]');
    if (!toggle) return;
    const wrap = toggle.closest('.wgh-cart__notes');
    if (!wrap) return;
    const open = !wrap.classList.contains('is-open');
    wrap.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', String(open));
    if (open) {
      const field = wrap.querySelector('textarea');
      if (field) field.focus();
    }
  });

  /* One-click add from a recommendation row. */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-wgh-rec-add]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    const id = Number(btn.getAttribute('data-wgh-rec-add'));
    if (!id) return;
    const idle = btn.dataset.label || btn.textContent;
    btn.disabled = true;
    btn.textContent = btn.dataset.labelBusy || idle;
    try {
      const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
      const res = await fetch(`${addUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }] })
      });
      if (!res.ok) throw new Error(`cart add failed: ${res.status}`);
      const done = () => {};
      /* modal.js: refreshes the drawer's main-cart; cart-counter.js: header count. */
      document.dispatchEvent(new CustomEvent('updateCart', { bubbles: true, composed: true, detail: { callback: done } }));
      /* /cart page: its own main-cart is not the drawer's copy. */
      const pageCart = Array.from(document.querySelectorAll('main-cart')).find((el) => !el.closest('modal-popup'));
      if (pageCart && typeof pageCart.update === 'function') pageCart.update(done);
    } catch (err) {
      console.error(err);
      btn.disabled = false;
      btn.textContent = idle;
    }
  });
})();
